/**
 * Trunnion AI - Node Palette
 * Left panel with draggable node types for the workflow designer.
 */

import { type DragEvent } from 'react';
import {
  Bot,
  UserCheck,
  GitBranch,
  GitFork,
  GitMerge,
  Zap,
  CircleStop,
  Workflow,
  GripVertical,
} from 'lucide-react';
import { nodePaletteItems, type NodePaletteItem } from '../nodes/nodeRegistry';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Bot,
  UserCheck,
  GitBranch,
  GitFork,
  GitMerge,
  Zap,
  CircleStop,
  Workflow,
};

const categoryLabels = {
  flow: 'Flow Control',
  execution: 'Execution',
  control: 'Control',
};

function PaletteItem({ item }: { item: NodePaletteItem }) {
  const Icon = iconMap[item.icon] ?? Zap;

  const onDragStart = (event: DragEvent) => {
    event.dataTransfer.setData('application/trunnion-node-type', item.type);
    event.dataTransfer.effectAllowed = 'move';
  };

  return (
    <div
      draggable
      onDragStart={onDragStart}
      className="flex items-center gap-2.5 rounded-lg border border-trunnion-slate-700 bg-trunnion-slate-800/50 px-3 py-2.5 cursor-grab active:cursor-grabbing hover:border-trunnion-gold-500/40 hover:bg-trunnion-slate-800 transition-colors group"
    >
      <GripVertical className="h-3.5 w-3.5 text-trunnion-slate-500 group-hover:text-trunnion-slate-400" />
      <Icon className="h-4 w-4 text-trunnion-gold-500" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white">{item.label}</p>
        <p className="text-xs text-trunnion-slate-400 truncate">
          {item.description}
        </p>
      </div>
    </div>
  );
}

export function NodePalette() {
  const categories = ['flow', 'execution', 'control'] as const;

  return (
    <div className="flex flex-col h-full w-64 border-r border-trunnion-slate-700 bg-trunnion-slate-900">
      <div className="border-b border-trunnion-slate-700 px-4 py-3">
        <h3 className="text-sm font-semibold text-white">Node Palette</h3>
        <p className="text-xs text-trunnion-slate-400 mt-0.5">
          Drag nodes onto the canvas
        </p>
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-4">
        {categories.map((category) => {
          const items = nodePaletteItems.filter((i) => i.category === category);
          if (items.length === 0) return null;
          return (
            <div key={category}>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-trunnion-slate-400">
                {categoryLabels[category]}
              </h4>
              <div className="space-y-1.5">
                {items.map((item) => (
                  <PaletteItem key={item.type} item={item} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
